import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import View from 'shared_features/athlete_popup';
import history from 'shared_redux/history';
import * as OrganizationsSelectors from 'shared_redux/selectors/models/organizations';
import * as AccountsSelectors from 'shared_redux/selectors/models/accounts';

/**
 * GroupInviteCreate component handles creating athletes via group invites for authenticated users.
 * 
 * This component:
 * - Requires authentication
 * - Uses the current user's email for the athlete
 * - Creates the athlete in the group specified by the invite
 * - Verifies passcode if the invite requires one
 */
const GroupInviteCreate = ({ isLoggedIn, userEmail, account }) => {
    const { code: inviteCode } = useParams();
    const [inviteInfo, setInviteInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [needsPasscode, setNeedsPasscode] = useState(false);
    const [passcode, setPasscode] = useState('');
    const [passcodeError, setPasscodeError] = useState(null);
    const [passcodeVerified, setPasscodeVerified] = useState(false);

    // Fetch invite information
    useEffect(() => {
        async function fetchInviteInfo() {
            setLoading(true);
            try {
                const response = await fetch(`/api/group-invite-links/${inviteCode}`);
                if (!response.ok) throw new Error('Invite not found');
                const data = await response.json();
                if (!data.team || !data.group) throw new Error('Invalid invite (missing team/group)');
                
                const inviteDetails = {
                    teamName: data.team.name,
                    groupName: data.group.name,
                    teamId: data.team.id,
                    groupId: data.group.id,
                    organizationName: data.organization?.name || 'Organization',
                    organizationLogo: data.organization?.logo_url || null,
                    hasPasscode: !!data.has_passcode
                };
                
                setInviteInfo(inviteDetails);
                setNeedsPasscode(inviteDetails.hasPasscode);
                
                if (!inviteDetails.hasPasscode) {
                    setPasscodeVerified(true);
                }
                
                setFormData(f => ({ 
                    ...f, 
                    team_id: data.team.id, 
                    group_id: data.group.id
                }));
            } catch (err) {
                setInviteInfo(null);
                setError(`Invalid or expired invite link: ${err.message}`);
            } finally {
                setLoading(false);
            }
        }
        if (inviteCode) fetchInviteInfo();
    }, [inviteCode, userEmail]);

    const verifyPasscode = async () => {
        setPasscodeError(null);
        
        // Trim the passcode and update state
        const trimmedPasscode = passcode.trim();
        setPasscode(trimmedPasscode);
        
        if (!trimmedPasscode) {
            setPasscodeError('Please enter the passcode');
            return;
        }
        
        // Validate passcode contains only alphanumeric characters
        if (!/^[a-zA-Z0-9]+$/.test(trimmedPasscode)) {
            setPasscodeError('Passcode must contain only letters and numbers');
            return;
        }
        
        try {
            // Generate CSRF token
            let csrfToken;
            try {
                const array = new Uint32Array(1);
                window.crypto.getRandomValues(array);
                csrfToken = array[0];
                
                // Set the CSRF cookie
                const inFifteenMinutes = new Date(new Date().getTime() + 15 * 60 * 1000);
                document.cookie = `csrf=${csrfToken}; expires=${inFifteenMinutes.toUTCString()}; path=/; secure; samesite=strict`;
            } catch (error) {
                console.error('Failed to generate CSRF token:', error);
            }
            
            const response = await fetch(`/api/group-invite-links/${inviteCode}/verify-passcode`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    passcode,
                    csrf: csrfToken 
                })
            });
            
            // Handle different response statuses
            if (!response.ok) {
                // Try to parse the error response
                const responseText = await response.text();
                let errorMessage = 'Error verifying passcode. Please try again.';
                
                try {
                    // Try to parse as JSON if possible
                    const errorData = JSON.parse(responseText);
                    
                    if (errorData.message) {
                        errorMessage = errorData.message;
                    } else if (errorData.error) {
                        errorMessage = errorData.error;
                    } else if (errorData.userMessage) {
                        errorMessage = errorData.userMessage;
                    }
                } catch (jsonError) {
                    // If it's not valid JSON, use the text directly
                    // This happens with some error responses that don't return JSON
                    if (responseText && responseText.length < 100) {
                        // Use the raw text if it's reasonably short
                        errorMessage = responseText;
                    } else if (response.status === 400 || response.status === 403) {
                        // Special handling for common passcode errors
                        errorMessage = 'Invalid passcode. Please try again.';
                    }
                }
                
                // Display the error message
                throw new Error(errorMessage);
            }
            
            // Success case
            const data = await response.json();
            setPasscodeVerified(true);
        } catch (err) {
            setPasscodeError(err.message || 'Invalid passcode');
        }
    };

    const handleFormChange = (field, value) => {
        // Don't allow email to be changed since we're using the authenticated user's email
        if (field !== 'email') {
            setFormData(f => ({ ...f, [field]: value }));
        }
    };
    
    const updateEmailError = (errorMessage) => {
        setError(errorMessage);
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        
        // No need to check for email since we're not sending it
        setError(null);
        try {
            // Create a copy of formData without the email property
            const { email, ...athleteData } = formData;
            
            // Include passcode in the request if needed
            const requestData = { 
                ...athleteData, 
                group_invite_code: inviteCode
            };
            
            if (needsPasscode) {
                // Use the trimmed passcode value
                const trimmedPasscode = passcode.trim();
                requestData.passcode = trimmedPasscode;
            }
            
            // Generate CSRF token
            let csrfToken;
            try {
                const array = new Uint32Array(1);
                window.crypto.getRandomValues(array);
                csrfToken = array[0];
                
                // Set the CSRF cookie
                const inFifteenMinutes = new Date(new Date().getTime() + 15 * 60 * 1000);
                document.cookie = `csrf=${csrfToken}; expires=${inFifteenMinutes.toUTCString()}; path=/; secure; samesite=strict`;
                
                // Add to request data - the server middleware will remove this before database insertion
                requestData.csrf = csrfToken;
            } catch (error) {
                console.error('Failed to generate CSRF token:', error);
            }
            
            const response = await fetch('/api/athletes', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });
            
            if (!response.ok) {
                // Try to extract the error message from the response
                try {
                    const errorData = await response.json();
                    throw new Error(`Failed to create athlete: ${errorData.message || errorData.error || 'Unknown error'}`);
                } catch (jsonError) {
                    // If we can't parse JSON, use the status text
                    throw new Error(`Failed to create athlete: ${response.statusText || 'Server error'}`);
                }
            }
            
            setSubmitted(true);
            // Redirect to athletes page after successful creation
            setTimeout(() => {
                history.push('/athletes');
            }, 2000);
        } catch (err) {
            setError(err.message || 'Submission failed');
        }
    };

    // Passcode entry screen
    const renderPasscodeForm = () => (
        <div className="container" style={{ maxWidth: 500, margin: '0 auto', padding: 20 }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
                {inviteInfo.organizationLogo && (
                    <img 
                        src={inviteInfo.organizationLogo} 
                        alt={`${inviteInfo.organizationName} Logo`} 
                        style={{ maxHeight: 60, maxWidth: '100%', marginBottom: 20 }} 
                    />
                )}
                <h2>Enter Passcode</h2>
                <p>
                    This invite link is protected. Please enter the passcode to continue.
                </p>
            </div>
            
            <div style={{ marginBottom: 20 }}>
                <label htmlFor="passcode" style={{ display: 'block', marginBottom: 5, fontWeight: 'bold' }}>
                    Passcode:
                </label>
                <input
                    id="passcode"
                    type="text"
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    style={{ 
                        width: '100%', 
                        padding: '10px', 
                        borderRadius: '4px',
                        border: '1px solid #ccc'
                    }}
                    placeholder="Enter passcode"
                    pattern="[a-zA-Z0-9]+"
                    title="Passcode must contain only letters and numbers"
                />
                {passcodeError && (
                    <div style={{ color: 'red', marginTop: 5 }}>{passcodeError}</div>
                )}
            </div>
            
            <div style={{ textAlign: 'center' }}>
                <button 
                    onClick={verifyPasscode}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#0066cc',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Continue
                </button>
            </div>
        </div>
    );

    if (loading) {
        return <div className="container"><div className="spinner">Loading...</div></div>;
    }
    
    if (!inviteInfo) {
        return (
            <div className="container">
                <div className="error-card">
                    <h2>Invalid Invite</h2>
                    <p>{error || 'Invalid or expired invite link.'}</p>
                    <button onClick={() => history.push('/athletes')}>
                        Return to Athletes
                    </button>
                </div>
            </div>
        );
    }
    
    if (needsPasscode && !passcodeVerified) {
        return renderPasscodeForm();
    }
    
    if (submitted) {
        return (
            <div className="container">
                <div className="success-card">
                    <h2>Success!</h2>
                    <p>Athlete created successfully. Redirecting to the athletes page...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: 32, maxWidth: 600, margin: '0 auto' }}>
            <h2>New Athlete</h2>
            {inviteInfo.organizationLogo && (
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <img 
                        src={inviteInfo.organizationLogo} 
                        alt={`${inviteInfo.organizationName} Logo`} 
                        style={{ maxHeight: 60, maxWidth: '100%' }} 
                    />
                </div>
            )}
            <p>
                You are creating an athlete for {inviteInfo.organizationName} in the group{' '}
                <strong>{inviteInfo.groupName}</strong> of team <strong>{inviteInfo.teamName}</strong>.
            </p>
            <View
                isPublic
                isShowing={true}
                teams={[{ id: inviteInfo.teamId, name: inviteInfo.teamName }]}
                groups={[{ id: inviteInfo.groupId, name: inviteInfo.groupName }]}
                selectedTeamId={inviteInfo.teamId}
                selectedGroupId={inviteInfo.groupId}
                groupId={inviteInfo.groupId}
                teamReadOnly
                groupReadOnly
                firstName={formData.first_name || ''}
                middleName={formData.middle_name || ''}
                lastName={formData.last_name || ''}
                email={userEmail || ''}
                updateFirstName={val => handleFormChange('first_name', val)}
                updateMiddleName={val => handleFormChange('middle_name', val)}
                updateLastName={val => handleFormChange('last_name', val)}
                updateEmail={val => handleFormChange('email', val)}
                updateEmailError={updateEmailError}
                save={handleSubmit}
                showAvatarOption={false}
                showRemove={false}
                emailError={error}
                emailReadOnly={true}
            />
        </div>
    );
};

const mapStateToProps = (state) => {
    const account = AccountsSelectors.getAccount(state);
    return {
        isLoggedIn: OrganizationsSelectors.getIsLoggedIn(state),
        userEmail: account?.email,
        account: account
    };
};

export default connect(mapStateToProps)(GroupInviteCreate); 